import { queryOptions, useQuery } from '@tanstack/react-query';

import type { DateRange } from '@cast/shared-types';
import { WorkloadLabelFilters } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_LABEL_VALUES_COST_REPORT_QUERY_KEY =
  'workloads-label-values-cost-report';

const getWorkloadLabelValuesQueryKey = ({
  startTime,
  endTime,
  label,
  filter,
}: {
  startTime?: string;
  endTime?: string;
  label?: string;
  filter?: string;
} = {}) =>
  [
    WORKLOADS_LABEL_VALUES_COST_REPORT_QUERY_KEY,
    startTime,
    endTime,
    label,
    filter,
  ] as const;

type WorkloadLabelValuesQuery = {
  label: string;
  clusterIds?: string[];
  range: DateRange;
  filter?: WorkloadLabelFilters;
  enabled?: boolean;
};

type UseWorkloadLabelValuesQuery = {
  isLoading: boolean;
  labelValues?: string[];
  error?: unknown;
  refetch: () => void;
};

const getWorkloadLabelValuesQueryOptions = ({
  label,
  range: [from, to],
  filter = {},
  enabled = true,
}: WorkloadLabelValuesQuery) =>
  queryOptions({
    retryOnMount: false,
    retry: false,
    queryKey: getWorkloadLabelValuesQueryKey({
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      label,
      filter: JSON.stringify(filter),
    }),
    queryFn: async () => {
      const values = [];
      let pageCursor = undefined;
      while (true) {
        const { data } = await apiClient.costReport.getWorkloadLabelValues(
          {
            label,
            startTime: from.toISOString(),
            endTime: to.toISOString(),
            labelFilters: filter,
            pageCursor,
          },
          { timeout: 30_000 }
        );
        if (data.labelValues) {
          values.push(...data.labelValues);
        }
        if (data.nextCursor) {
          pageCursor = data.nextCursor;
        } else {
          break;
        }
      }
      return values;
    },
    enabled,
  });

export const useWorkloadLabelValuesQuery = (
  props: WorkloadLabelValuesQuery
): UseWorkloadLabelValuesQuery => {
  const {
    isLoading,
    data: labelValues,
    error,
    refetch,
  } = useQuery(getWorkloadLabelValuesQueryOptions(props));

  return {
    labelValues,
    isLoading,
    error,
    refetch,
  };
};
